import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon, BookOpenIcon, ChartBarIcon, CheckIcon, LockClosedIcon, PlayIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import GameTutorial from '../../pages/Games/GameTutorial';
import {
  AppPage, BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Eyebrow, HeroPanel, Pill, Segmented,
} from '../UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Level colours shared by the picker, chart points and run pills
export const LEVEL_TONES = {
  green: { hex: '#22C55E', pill: 'green', badge: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300', ring: 'ring-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-500' },
  yellow: { hex: '#EAB308', pill: 'amber', badge: 'bg-amber-500/15 text-amber-600 dark:text-amber-300', ring: 'ring-amber-500', btn: 'bg-amber-600 hover:bg-amber-500' },
  rose: { hex: '#F43F5E', pill: 'red', badge: 'bg-rose-500/15 text-rose-600 dark:text-rose-300', ring: 'ring-rose-500', btn: 'bg-rose-600 hover:bg-rose-500' },
};
const DEFAULT_LEVEL_COLORS = { 1: 'green', 2: 'yellow', 3: 'rose' };

// Text colour for a 0–100 score
export const scoreTone = (pct) => (pct >= 80 ? 'text-emerald-600 dark:text-emerald-400'
  : pct >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400');

export const runDate = (run, opts = { weekday: 'short', month: 'short', day: 'numeric' }) => new Date(run.completed_at).toLocaleDateString('en-US', opts);

// Loads a game's stats and access (same endpoints the original setup pages use)
export const useGameStats = (code, subjectId) => {
  const slug = code.toLowerCase();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);

  const fetchStats = async () => {
    try {
      setStats(await apiClient.get(`/api/courses/${slug}-game/stats/${subjectId}/`));
    } catch (error) {
      // No stats yet: the page shows its empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    apiClient.get(`/api/payments/game-access/${code}/`)
      .then((data) => setHasAccess(data.has_access))
      .catch(() => setHasAccess(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  const reset = async () => {
    await apiClient.post(`/api/courses/${slug}-game/reset/${subjectId}/`);
    await fetchStats();
  };

  return { stats, loading, hasAccess, reset };
};

// Header of a selected run: date, level and the big score
export const RunHeader = ({ run, level, levelColor, score, scoreLabel, sub }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div>
      <p className="font-semibold text-slate-900 dark:text-white">{runDate(run)}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {level && <Pill tone={LEVEL_TONES[levelColor || DEFAULT_LEVEL_COLORS[level]]?.pill || 'gray'}>Level {level}</Pill>}
        {sub && <span className="text-xs text-slate-500 dark:text-gray-400">{sub}</span>}
      </div>
    </div>
    <div className="text-right">
      <p className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">{score}</p>
      <p className="text-xs text-slate-500 dark:text-gray-400">{scoreLabel}</p>
    </div>
  </div>
);

// Small tiles (R1, R2, ...); items are { label, value, tone: 'good' | 'mid' | 'bad' | undefined, sub }
const TILE_TONES = {
  good: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  mid: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  bad: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
};
export const TileGrid = ({ title, items }) => (
  <div>
    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-gray-500">{title}</p>
    <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-8 xl:grid-cols-10">
      {items.map((it, i) => (
        <div key={i} className={`rounded-lg border px-1 py-1.5 text-center ${TILE_TONES[it.tone] || 'border-slate-200 bg-slate-50 text-slate-900 dark:border-white/10 dark:bg-white/[0.04] dark:text-white'}`}>
          <p className="text-[11px] opacity-60">{it.label}</p>
          <p className="text-sm font-semibold">{it.value}</p>
          {it.sub && <p className="text-[11px] opacity-60">{it.sub}</p>}
        </div>
      ))}
    </div>
  </div>
);

// Labelled numbers in a row (e.g. accuracy by rule)
export const StatGrid = ({ items, cols = 'grid-cols-2 sm:grid-cols-4' }) => (
  <div className={`grid gap-2 ${cols}`}>
    {items.map((it) => (
      <div key={it.label} className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-xs text-slate-500 dark:text-gray-400">{it.label}</p>
        <p className={`mt-0.5 text-lg font-semibold ${it.className || 'text-slate-900 dark:text-white'}`}>{it.value}</p>
        {it.sub && <p className="text-xs capitalize text-slate-500 dark:text-gray-500">{it.sub}</p>}
      </div>
    ))}
  </div>
);

// Runs over time; series are { label, color, value(run), main } and the main series gets level-coloured points
const RunChart = ({ runs, series, max, suffix = '', levelOf, levelColors, selectedId, onSelect }) => {
  const H = 220;
  const pad = { left: 24, right: 24, top: 16, bottom: 28 };
  const spacing = 56;
  const usable = H - pad.top - pad.bottom;
  const width = Math.max(480, pad.left + pad.right + (runs.length - 1) * spacing);
  const y = (v) => pad.top + ((max - Math.min(Math.max(v, 0), max)) / max) * usable;
  const step = max > 15 ? max / 5 : max > 8 ? 2 : 1;
  const ticks = [];
  for (let t = max; t >= 0; t -= step) ticks.push(Math.round(t * 10) / 10);
  const main = series.find((s) => s.main) || series[0];
  const scroller = useRef(null);

  // Open on the newest runs when the chart is wider than its card (phones, long histories)
  useEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [runs.length]);

  return (
    <div className="relative flex">
      <div className="flex w-10 flex-shrink-0 flex-col text-right text-[11px] text-slate-400 dark:text-gray-500" style={{ height: H }}>
        {ticks.map((t) => (
          <span key={t} className="absolute -translate-y-1/2 pr-1" style={{ top: y(t), width: 40, left: 0 }}>{t}{suffix}</span>
        ))}
      </div>
      <div ref={scroller} className="min-w-0 flex-1 overflow-x-auto">
        <svg width={width} height={H} className="block text-slate-300 dark:text-white/10">
          {ticks.map((t) => (
            <line key={t} x1={0} x2={width} y1={y(t)} y2={y(t)} stroke="currentColor" strokeDasharray="4 4" />
          ))}
          {runs.length > 1 && series.map((s) => (
            <polyline
              key={s.label}
              points={runs.map((r, i) => `${pad.left + i * spacing},${y(s.value(r))}`).join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={s === main ? 2.5 : 1.5}
              strokeOpacity={s === main ? 0.95 : 0.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
          {runs.map((r, i) => {
            const selected = selectedId === r.id;
            const cx = pad.left + i * spacing;
            return (
              <g
                key={r.id ?? i}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-label={`Run ${i + 1}, ${runDate(r)}: ${Math.round(main.value(r) * 10) / 10}${suffix}`}
                className="cursor-pointer focus:outline-none"
                onClick={() => onSelect(selected ? null : r)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(selected ? null : r); } }}
              >
                <circle cx={cx} cy={y(main.value(r))} r={14} fill="transparent" />
                <circle
                  cx={cx}
                  cy={y(main.value(r))}
                  r={selected ? 7 : 5}
                  fill={LEVEL_TONES[levelColors[levelOf(r)]]?.hex || main.color}
                  className="stroke-white dark:stroke-slate-950"
                  strokeWidth={2}
                />
                {selected && <circle cx={cx} cy={y(main.value(r))} r={11} fill="none" stroke={LEVEL_TONES[levelColors[levelOf(r)]]?.hex || main.color} strokeOpacity={0.5} strokeWidth={2} />}
                <text x={cx} y={H - 8} textAnchor="middle" className="fill-slate-400 text-[11px] dark:fill-gray-500">{i + 1}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

// Selectable level cards (radio group)
export const LevelPicker = ({ levels, value, onChange }) => (
  <div role="radiogroup" aria-label="Level" className="space-y-2">
    {levels.map((l) => {
      const tone = LEVEL_TONES[l.color];
      const active = value === l.id;
      return (
        <button
          key={l.id}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(l.id)}
          className={`w-full rounded-xl border p-3 text-left transition-colors ${active
            ? `border-transparent bg-white ring-2 ${tone.ring} dark:bg-white/[0.06]`
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:hover:border-white/20 dark:hover:bg-white/[0.04]'}`}
        >
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold ${tone.badge}`}>{l.id}</span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 dark:text-white">{l.description}</p>
              <p className="text-xs text-slate-500 dark:text-gray-400">{l.name}</p>
            </div>
            {active && <CheckIcon className="h-5 w-5 flex-shrink-0 text-slate-900 dark:text-white" />}
          </div>
          {active && (
            <ul className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-sm text-slate-600 dark:border-white/10 dark:text-gray-300">
              {l.details.map((d) => (
                <li key={d} className="flex gap-2"><span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-current opacity-60" />{d}</li>
              ))}
            </ul>
          )}
        </button>
      );
    })}
  </div>
);

export const startButtonClass = (color) => `${BTN_PRIMARY} w-full py-3 text-base ${color ? LEVEL_TONES[color].btn : ''}`;

const imageUrl = (subject) => {
  const src = subject?.banner_image || subject?.image;
  if (!src) return null;
  return src.startsWith('http') ? src : `${API_URL}${src}`;
};

/*
  The redesigned game setup page. Pages pass their own data and pieces:
  - code / name / description: fallbacks until the subject loads
  - game: the useGameStats() result; heroStats: [{ label, value }]
  - picker: the level/exercise picker and start button (right column)
  - runs + chart: { series, max, suffix } for the history chart; levelOf(run) for colouring and filtering
  - insights: optional block above the run details; renderRun(run): the selected run's details
*/
const GameSetupShell = ({
  code, name, description, game, heroStats = [], picker,
  runs = [], chart, levelOf = (r) => r.level || 1, filterLevels = [1, 2, 3], levelColors = DEFAULT_LEVEL_COLORS,
  insights, renderRun,
}) => {
  const { stats, loading, hasAccess, reset } = game;
  const subject = stats?.subject;
  const [tab, setTab] = useState('performance');
  const [levelFilter, setLevelFilter] = useState('all');
  const [selectedRun, setSelectedRun] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const shownRuns = useMemo(() => {
    const sorted = [...runs].sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));
    return levelFilter === 'all' ? sorted : sorted.filter((r) => levelOf(r) === Number(levelFilter));
  }, [runs, levelFilter, levelOf]);

  useEffect(() => {
    if (!confirmReset) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setConfirmReset(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmReset]);

  const doReset = async () => {
    setResetting(true);
    try {
      await reset();
      setSelectedRun(null);
      setConfirmReset(false);
    } catch (error) {
      console.error('Failed to reset progress:', error);
    } finally {
      setResetting(false);
    }
  };

  const title = subject?.name || name;
  const hero = (
    <HeroPanel image={imageUrl(subject)}>
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white">
        <ArrowLeftIcon className="h-4 w-4" />Dashboard
      </Link>
      <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-white/10 px-2 py-0.5 text-xs font-bold tracking-wider text-white ring-1 ring-white/20">{subject?.code || code}</span>
            <Eyebrow className="!text-primary-300">Screening game</Eyebrow>
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-4xl">{title.replace(/^[A-Z]{2,4}\s*-\s*/, '')}</h1>
          <p className="mt-2 text-gray-300">{subject?.description || description}</p>
        </div>
        {hasAccess !== false && heroStats.length > 0 && (
          <dl className="grid grid-cols-3 gap-2 sm:gap-3">
            {heroStats.map((s) => (
              <div key={s.label} className="min-w-[84px] rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2.5 backdrop-blur">
                <dt className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">{s.label}</dt>
                <dd className="mt-0.5 text-xl font-semibold text-white">{loading ? '–' : s.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </HeroPanel>
  );

  if (hasAccess === false) {
    return (
      <AppPage wide>
        {hero}
        <div className={`${CARD} mx-auto mt-6 max-w-xl`}>
          <Empty
            icon={LockClosedIcon}
            title={`Unlock the ${subject?.code || code} game`}
            action={<Link to="/store" className={BTN_PRIMARY}>Unlock in the store</Link>}
          >
            Add this screening game to your DLR plan to practise it here and track every run.
          </Empty>
        </div>
      </AppPage>
    );
  }

  return (
    <AppPage wide>
      {hero}

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Level / exercise picker: first on phones so Start is near the top */}
        <aside className={`${CARD} order-first p-4 sm:p-5 lg:sticky lg:top-24 lg:order-none lg:col-start-2 lg:row-start-1`}>
          {picker}
        </aside>

        <section className={`${CARD} min-w-0 lg:col-start-1 lg:row-start-1`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4 sm:px-5 dark:border-white/10">
            <Segmented
              label="Section"
              size="md"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'performance', label: <span className="inline-flex items-center gap-1.5"><ChartBarIcon className="h-4 w-4" />Performance</span> },
                { value: 'tutorial', label: <span className="inline-flex items-center gap-1.5"><BookOpenIcon className="h-4 w-4" />How it works</span> },
              ]}
            />
            {tab === 'performance' && (
              <div className="flex flex-wrap items-center gap-2">
                <Segmented
                  label="Filter by level"
                  value={levelFilter}
                  onChange={(v) => { setLevelFilter(v); setSelectedRun(null); }}
                  options={[{ value: 'all', label: 'All' }, ...filterLevels.map((l) => ({ value: String(l), label: `L${l}` }))]}
                />
                {stats?.total_runs > 0 && (
                  <button onClick={() => setConfirmReset(true)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-500/10 dark:text-rose-400">
                    <TrashIcon className="h-4 w-4" />Reset
                  </button>
                )}
              </div>
            )}
          </div>

          {tab === 'performance' ? (
            <div className="space-y-5 p-4 sm:p-5">
              {loading ? (
                <div className="h-[220px] animate-pulse rounded-xl bg-slate-100 dark:bg-white/[0.04]" />
              ) : shownRuns.length === 0 ? (
                <Empty icon={ChartBarIcon} title={runs.length ? 'No runs at this level yet' : 'No runs yet'}>
                  {runs.length ? 'Pick another level above, or play this one to see it here.' : 'Finish your first run and your scores will be charted here.'}
                </Empty>
              ) : (
                <>
                  <div>
                    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-gray-400">
                      {chart.series.length > 1 && chart.series.map((s) => (
                        <span key={s.label} className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded" style={{ background: s.color, opacity: s.main ? 1 : 0.6 }} />{s.label}</span>
                      ))}
                      {filterLevels.map((l) => (
                        <span key={l} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: LEVEL_TONES[levelColors[l]].hex }} />Level {l}</span>
                      ))}
                      <span className="ml-auto hidden sm:inline">Tap a point for details</span>
                    </div>
                    <RunChart
                      runs={shownRuns}
                      series={chart.series}
                      max={typeof chart.max === 'function' ? chart.max(shownRuns) : chart.max}
                      suffix={chart.suffix}
                      levelOf={levelOf}
                      levelColors={levelColors}
                      selectedId={selectedRun?.id}
                      onSelect={setSelectedRun}
                    />
                  </div>
                  {insights}
                  <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10">
                    {selectedRun ? renderRun(selectedRun) : (
                      <p className="py-6 text-center text-sm text-slate-500 dark:text-gray-400">Select a point on the chart to see that run in detail.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="p-4 sm:p-5">
              <GameTutorial gameCode={code} />
            </div>
          )}
        </section>
      </div>

      {confirmReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={() => setConfirmReset(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="reset-title" className={`${CARD} w-full max-w-md p-6 shadow-2xl dark:bg-slate-900`} onClick={(e) => e.stopPropagation()}>
            <h2 id="reset-title" className="text-lg font-semibold text-slate-900 dark:text-white">Reset your {subject?.code || code} progress?</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
              This deletes all {stats?.total_runs || 0} runs for this game. It can&apos;t be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setConfirmReset(false)} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={doReset} disabled={resetting} className={BTN_DANGER}>{resetting ? 'Resetting…' : 'Reset progress'}</button>
            </div>
          </div>
        </div>
      )}
    </AppPage>
  );
};

// Standard picker for the level-based games
export const LevelStart = ({ levels, value, onChange, onStart, children }) => {
  const current = levels.find((l) => l.id === value);
  return (
    <>
      <Eyebrow>Choose a level</Eyebrow>
      <div className="mt-3"><LevelPicker levels={levels} value={value} onChange={onChange} /></div>
      {children}
      <button onClick={onStart} className={`${startButtonClass(current.color)} mt-4`}>
        <PlayIcon className="h-5 w-5" />Start {current.name}
      </button>
    </>
  );
};

export default GameSetupShell;
